import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { ToggleGroup, ToggleGroupItem } from "@radix-ui/react-toggle-group";
import { cx } from "class-variance-authority";
import { Check } from "lucide-react";

export const TechPopover = ({
  children,
  label,
  disabledReason,
}: {
  children: React.ReactNode;
  label: React.ReactNode;
  disabledReason?: string;
}) => {
  return (
    <Popover>
      <PopoverTrigger
        disabled={Boolean(disabledReason)}
        data-tooltip-id="tooltip"
        data-tooltip-content={disabledReason}
        className={cx(
          "flex h-8 w-full gap-2 rounded-md cursor-pointer items-center text-xs focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          "bg-secondary text-text-muted px-2 hover:bg-secondary-200 data-[state=open]:bg-secondary-300 data-[state=open]:text-text-primary",
        )}
      >
        {label}
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className={cx(
            "bg-popover border rounded-md shadow-md z-5050 text-popover-foreground",
            "max-h-(--radix-popover-content-available-height) max-w-(--radix-popover-content-available-width) min-w-(--radix-popover-trigger-width)",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=bottom]:translate-y-1",
            "data-[side=left]:-translate-x-1 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=right]:translate-x-1",
            "data-[side=top]:-translate-y-1 data-[side=top]:slide-in-from-bottom-2",
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
            "flex flex-col gap-1 p-2",
          )}
        >
          {children}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export const TechPopoverGroup = ({
  children,
  label,
  value,
  onValueChange,
}: {
  children: React.ReactNode;
  label: React.ReactNode;
  value: string | undefined;
  onValueChange: (value: string | undefined) => void;
}) => {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(value) => onValueChange(value)}
      className="flex flex-col gap-1 px-2 -mx-2"
    >
      <div className="px-2 text-2xs text-text-secondary font-medium">
        {label}
      </div>
      {children}
    </ToggleGroup>
  );
};

export const TechPopoverItem = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: string;
}) => {
  return (
    <ToggleGroupItem
      value={value}
      className={cx(
        "group cursor-pointer relative flex w-full select-none items-center rounded-sm py-1.5 px-2 text-xs gap-3 outline-hidden",
        "hover:bg-secondary focus:bg-secondary focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
      )}
    >
      <div
        className={cx(
          "h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
          "group-data-[state=on]:bg-accent group-data-[state=on]:border-accent group-data-[state=on]:text-accent-foreground",
        )}
      >
        <Check className="h-3 w-3 group-data-[state=on]:block hidden" />
      </div>
      {children}
    </ToggleGroupItem>
  );
};
